import { useMemo } from 'react'
import usePersonalPortalHref from 'hooks/usePersonalPortalHref'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTimeWindowByDate } from 'lib/datetime/dateUtils'
import config from 'constants/config'
import { toOrdinal } from 'lib/maths/mathUtils'
import { getPlural } from 'lib/string/pluralize'
import useSocieteCampaignTargetTier from './useSocieteCampaignTargetTier'
import mapSocieteBusinessSettings from 'businessTraveller/api/mappers/businessSettingMapper'

/**
 * Dynamic societe variables used to interpolate societe content text.
 */
export interface SocieteVariables {
  /** The tier of the campaign - Default 'silver' */
  targetTier: App.LoyaltyTier
  /** The display name of the target tier - Default 'Silver' */
  targetTierDisplayName: string
  /** Number of bookings required to unlock the target Société tier - Default 5 */
  bookingThreshold: number
  /** Ordinal form of `bookingThreshold` - e.g. 'fifth', '11th', '21st' */
  bookingThresholdOrdinal: string
  /** Plural form of "booking" matching `bookingThreshold` — e.g. "booking" for 1, "bookings" otherwise. */
  bookingPluralText: string
  /** The end date of the qualification derived from employee activation date - Default 12 months later */
  qualificationEndDate: string
  /** Absolute URL of the LE consumer-portal Société page. */
  societePageHref: string
  /** The given name of the employee - Default '' */
  givenName: string
}

export function useSocieteVariables(): SocieteVariables {
  const { targetTier, targetTierDisplayName } = useSocieteCampaignTargetTier()
  const defaultThreshold = config.businessTraveller.defaultSocieteBusinessSettings[targetTier].threshold
  const personalPortalHref = usePersonalPortalHref()
  const societeSetting = useAppSelector(state => state.businessTraveller.businessSettings?.businessSettings?.societe)
  const employeeActivatedAt = useAppSelector(state => state.businessTraveller.currentBusinessAccount?.employee?.activatedAt)
  const givenName = useAppSelector(state => state.auth.account.givenName)

  const societeVariables = useMemo(() => {
    const { endDate } = getTimeWindowByDate(employeeActivatedAt ?? new Date())
    const societeTierSetting = mapSocieteBusinessSettings(societeSetting)?.[targetTier]
    const bookingThreshold = societeTierSetting?.threshold ?? defaultThreshold

    return {
      targetTier,
      targetTierDisplayName,
      bookingThreshold,
      bookingThresholdOrdinal: toOrdinal(bookingThreshold),
      bookingPluralText: getPlural('booking', bookingThreshold),
      qualificationEndDate: endDate,
      societePageHref: `${personalPortalHref}/societe`,
      givenName,
    }
  }, [societeSetting, employeeActivatedAt, personalPortalHref, defaultThreshold, targetTier, targetTierDisplayName, givenName])

  return societeVariables
}
